import type { UppyFile } from '@uppy/core';

/**
 * Uppy metadata type - file metadata that can be attached to uploads.
 */
export type UppyMeta = Record<string, unknown>;

/**
 * Uppy response body type - data returned from the upload endpoint.
 */
export type UppyBody = Record<string, unknown>;

/**
 * Attachment data returned from successful uploads.
 */
export interface AttachmentData {
  id?: string;
  url?: string;
  name?: string;
  size?: number;
  [key: string]: unknown;
}

/**
 * File upload progress state for UI display.
 * Used by both single-file and multi-file uploaders to expose upload state to UI components.
 */
export interface FileInProgress {
  /** Upload progress as a percentage (0-100) */
  progress: number;
  /** File name */
  name: string;
  /** File size in bytes */
  size: number;
  /** File data blob for generating preview URLs */
  data?: Blob;
}

/**
 * File upload error state for UI display.
 * Includes file metadata so errors can display the file name and size.
 */
export interface FileUploadError {
  /** The error that occurred during upload */
  error: Error;
  /** File name */
  name: string;
  /** File size in bytes */
  size: number;
}

/**
 * These event types manage internal state machine events (Uppy → XState)
 */
export const EVENT_TYPES = {
  INITIALIZE: 'INITIALIZE',
  FILE_ADDED: 'FILE_ADDED',
  UPLOAD_PROGRESS: 'UPLOAD_PROGRESS',
  UPLOAD_SUCCESS: 'UPLOAD_SUCCESS',
  UPLOAD_COMPLETE: 'UPLOAD_COMPLETE',
  UPLOAD_ERROR: 'UPLOAD_ERROR',
  RESTRICTION_FAILED: 'RESTRICTION_FAILED',
  FILE_REMOVED: 'FILE_REMOVED',
  CANCEL_ALL: 'CANCEL_ALL',
  REMOVE_FILE: 'REMOVE_FILE',
  FINALIZE_START: 'FINALIZE_START',
  FINALIZE_COMPLETE: 'FINALIZE_COMPLETE',
  FINALIZE_ERROR: 'FINALIZE_ERROR',
  CLEAR_ERROR: 'CLEAR_ERROR',
  RESET: 'RESET',
  FILE_SIZE_UPDATED: 'FILE_SIZE_UPDATED',
} as const;

/**
 * These are external API type definitions (for hooks/components consuming the machine)
 */
export const MULTI_FILE_EVENT_TYPES = {
  /** Multiple files added to the uploader */
  FILES_ADDED: 'FILES_ADDED',
  /** Progress update for a specific file */
  FILE_UPLOAD_PROGRESS: 'FILE_UPLOAD_PROGRESS',
  /** A file upload completed successfully */
  FILE_UPLOAD_SUCCESS: 'FILE_UPLOAD_SUCCESS',
  /** A file upload failed */
  FILE_UPLOAD_ERROR: 'FILE_UPLOAD_ERROR',
  /** All uploads completed (success or failure) */
  ALL_UPLOADS_COMPLETE: 'ALL_UPLOADS_COMPLETE',
  /** File restriction validation failed */
  FILES_RESTRICTION_FAILED: 'FILES_RESTRICTION_FAILED',
  /** Cancel all in-progress uploads */
  CANCEL_ALL_UPLOADS: 'CANCEL_ALL_UPLOADS',
  /** Remove a specific file from the queue */
  REMOVE_FILE: 'REMOVE_FILE',
  /** Reset uploader to initial state */
  RESET_UPLOADER: 'RESET_UPLOADER',
} as const;

/**
 * Validation result for file restrictions
 */
export interface ValidationResult {
  valid: boolean;
  error?: Error;
}

/**
 * Upload restrictions configuration
 */
export interface UploaderRestrictions {
  allowedFileTypes?: string[] | null;
  maxFileSize?: number;
  maxNumberOfFiles?: number;
  minFileSize?: number;
  maxTotalFileSize?: number;
  minNumberOfFiles?: number;
  endpoint?: string;
}

/**
 * Callback signature for custom upload parameter resolution.
 */
export type UploadParamsCallback = (fileMetadata: { meta: { name: string; size: number }; file: File }) => Promise<{
  method: string;
  body: File | FormData;
  url: string;
  headers: Record<string, string>;
  attachmentId?: string;
}>;

/**
 * Shared configuration fields used by both InitializeConfig (public API)
 * and InitializeEvent (internal state machine event).
 */
interface BaseInitializeFields {
  restrictions?: UploaderRestrictions;
  onUploadParams?: UploadParamsCallback;
  onFinalize?: () => Promise<void>;
  onBackboneSync?: (attachmentData: AttachmentData) => void;
  persist?: boolean;
  dragDropTarget?: HTMLElement;
  /** Current total size of already uploaded files for validation (bytes) */
  currentTotalSize?: number;
  /** Current count of already uploaded files for validation */
  currentFileCount?: number;
  /** Enable multi-file upload mode with concurrent uploads */
  multiFile?: boolean;
  /**
   * Maximum image dimension (width or height) in pixels. When set, images exceeding
   * this bound are resized before upload. Omit to skip compression entirely.
   */
  downscaleTo?: number;
  /**
   * Allow files with duplicate names to be added to the same upload batch.
   * When true, duplicate files are given unique IDs instead of being rejected.
   * @default false
   */
  allowDuplicates?: boolean;
}

/**
 * Configuration for initializing the uploader
 */
export interface InitializeConfig extends BaseInitializeFields {}

/**
 * Event type definitions for the state machine
 */
export interface InitializeEvent extends BaseInitializeFields {
  type: 'INITIALIZE';
}

export interface FileAddedEvent {
  type: 'FILE_ADDED';
  file: UppyFile<UppyMeta, UppyBody>;
}

export interface UploadProgressEvent {
  type: 'UPLOAD_PROGRESS';
  file: UppyFile<UppyMeta, UppyBody>;
  progress: {
    bytesUploaded: number;
    bytesTotal: number;
  };
}

export interface UploadSuccessEvent {
  type: 'UPLOAD_SUCCESS';
  file: UppyFile<UppyMeta, UppyBody>;
  response: {
    body?: UppyBody;
    status: number;
    bytesUploaded?: number;
    uploadURL?: string;
  };
  attachmentId?: string;
}

export interface UploadCompleteEvent {
  type: 'UPLOAD_COMPLETE';
  result: {
    successful?: UppyFile<UppyMeta, UppyBody>[];
    failed?: UppyFile<UppyMeta, UppyBody>[];
  };
}

export interface UploadErrorEvent {
  type: 'UPLOAD_ERROR';
  file?: UppyFile<UppyMeta, UppyBody>;
  error: Error;
}

export interface RestrictionFailedEvent {
  type: 'RESTRICTION_FAILED';
  file?: UppyFile<UppyMeta, UppyBody>;
  error: Error;
}

export interface FileRemovedEvent {
  type: 'FILE_REMOVED';
  file?: UppyFile<UppyMeta, UppyBody>;
}

export interface CancelAllEvent {
  type: 'CANCEL_ALL';
}

export interface RemoveFileEvent {
  type: 'REMOVE_FILE';
}

export interface FinalizeStartEvent {
  type: 'FINALIZE_START';
}

export interface FinalizeCompleteEvent {
  type: 'FINALIZE_COMPLETE';
}

export interface FinalizeErrorEvent {
  type: 'FINALIZE_ERROR';
  error: Error;
}

export interface ClearErrorEvent {
  type: 'CLEAR_ERROR';
}

export interface ResetEvent {
  type: 'RESET';
}

/**
 * Sent after a file is resized before upload so the machine reflects
 * the actual byte count rather than the original pre-compression size.
 */
export interface FileSizeUpdatedEvent {
  type: 'FILE_SIZE_UPDATED';
  /** The Uppy file whose size changed (same reference as the FILE_ADDED event) */
  file: UppyFile<UppyMeta, UppyBody>;
  /** New file size in bytes after resize */
  size: number;
}

export type UniversalMediaUploaderEvent =
  | InitializeEvent
  | FileAddedEvent
  | UploadProgressEvent
  | UploadSuccessEvent
  | UploadCompleteEvent
  | UploadErrorEvent
  | RestrictionFailedEvent
  | FileRemovedEvent
  | CancelAllEvent
  | RemoveFileEvent
  | FinalizeStartEvent
  | FinalizeCompleteEvent
  | FinalizeErrorEvent
  | ClearErrorEvent
  | ResetEvent
  | FileSizeUpdatedEvent;
